import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { AiFillSetting } from "react-icons/ai";
import DynamicAnalysisChartsPanel from "./DynamicAnalysisChartsPanel";
import MapLoadIndicator from "./MapLoadIndicator";

const FRAME_SOURCE = "/map_runtime/compare_frame.html";

function mergeCompareRows(leftRows = [], rightRows = []) {
  const rowsByName = new Map();
  const collect = (rows, field) => {
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const name = String(row?.name || "").trim();
      if (!name) return;
      const nextRow = rowsByName.get(name) || { name, periodA: 0, periodB: 0 };
      nextRow[field] = Number(row?.value || 0);
      rowsByName.set(name, nextRow);
    });
  };

  collect(leftRows, "periodA");
  collect(rightRows, "periodB");

  return Array.from(rowsByName.values()).sort((a, b) => {
    const byTotal = b.periodA + b.periodB - (a.periodA + a.periodB);
    if (byTotal) return byTotal;
    return a.name.localeCompare(b.name, "ru");
  });
}

function combineComparisonChartsData(sides) {
  const left = sides?.left || null;
  const right = sides?.right || null;
  if (!left && !right) return null;

  const leftCount = Number(left?.count || 0);
  const rightCount = Number(right?.count || 0);
  const leftSummary = left?.severitySummary || {};
  const rightSummary = right?.severitySummary || {};
  const leftPeriod = left?.periodLabel || "левая карта";
  const rightPeriod = right?.periodLabel || "правая карта";
  const leftScope = left?.scopeLabel || "";
  const rightScope = right?.scopeLabel || "";
  const scopeLabel = leftScope && leftScope === rightScope ? leftScope : [leftScope, rightScope].filter(Boolean).join(" / ");
  const message = left?.message || right?.message || "Недостаточно данных для построения графиков";
  const totalHexCount = Math.max(
    Number(left?.meta?.totalHexCount || 0),
    Number(right?.meta?.totalHexCount || 0)
  );

  if (!totalHexCount) {
    return {
      valid: Boolean((left?.valid ?? true) && (right?.valid ?? true)),
      scopeLabel: [`A: ${leftPeriod}; B: ${rightPeriod}`, scopeLabel].filter(Boolean).join(" · "),
      message,
      overview: [],
      types: [],
      severity: [],
      severitySummary: null,
    };
  }

  return {
    valid: Boolean((left?.valid ?? true) && (right?.valid ?? true)),
    scopeLabel: [`A: ${leftPeriod}; B: ${rightPeriod}`, scopeLabel].filter(Boolean).join(" · "),
    message,
    overview: [
      { name: "Период A", value: leftCount },
      { name: "Период B", value: rightCount },
    ],
    types: mergeCompareRows(left?.types, right?.types),
    severity: mergeCompareRows(left?.severity, right?.severity),
    severitySummary: {
      periodAScore: Number(leftSummary.score || 0),
      periodBScore: Number(rightSummary.score || 0),
      periodACoefficient: Number(leftSummary.coefficient || 0),
      periodBCoefficient: Number(rightSummary.coefficient || 0),
    },
  };
}

function buildFrameUrl(side) {
  const params = new URLSearchParams({
    side,
    hideShared: "1",
  });
  return `${FRAME_SOURCE}?${params.toString()}`;
}

function postToFrame(frameRef, payload) {
  frameRef.current?.contentWindow?.postMessage(
    {
      source: "compare-map-parent",
      ...payload,
    },
    window.location.origin
  );
}

export default function ComparisonWorkspace() {
  const leftFrameRef = useRef(null);
  const rightFrameRef = useRef(null);
  const sharedStateRef = useRef(null);
  const districtStateRef = useRef([]);
  const hexSelectionRef = useRef({ hexIds: [], activeHexId: "" });
  const viewportStateRef = useRef(null);
  const chartsDataBySideRef = useRef({ left: null, right: null });
  const [activeSharedDrawer, setActiveSharedDrawer] = useState("");
  const [chartsData, setChartsData] = useState(null);
  const [mapLoadingBySide, setMapLoadingBySide] = useState({});

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data || {};
      if (payload.source !== "compare-map-frame") return;

      const targetFrameRef = payload.side === "left" ? leftFrameRef : rightFrameRef;
      const peerFrameRef = payload.side === "left" ? rightFrameRef : leftFrameRef;

      if (payload.type === "compare-frame-ready") {
        if (sharedStateRef.current) {
          postToFrame(targetFrameRef, {
            type: "apply-shared-state",
            state: sharedStateRef.current,
          });
        }
        if (districtStateRef.current?.length) {
          postToFrame(targetFrameRef, {
            type: "apply-district-selection",
            districts: districtStateRef.current,
          });
        }
        if (hexSelectionRef.current?.hexIds?.length) {
          postToFrame(targetFrameRef, {
            type: "apply-hex-selection",
            selection: hexSelectionRef.current,
          });
        }
        if (viewportStateRef.current) {
          postToFrame(targetFrameRef, {
            type: "set-viewport",
            viewport: viewportStateRef.current,
          });
        }
        return;
      }

      if (payload.type === "shared-drawer-visibility") {
        setActiveSharedDrawer(payload.open ? String(payload.drawer || "") : "");
        return;
      }

      if (payload.type === "map-loading-state") {
        setMapLoadingBySide((current) => ({
          ...current,
          [payload.side]: payload.state || null,
        }));
        return;
      }

      if (payload.type === "compare-map-charts-data") {
        if (payload.side === "left" || payload.side === "right") {
          chartsDataBySideRef.current = {
            ...chartsDataBySideRef.current,
            [payload.side]: payload.data || null,
          };
          setChartsData(combineComparisonChartsData(chartsDataBySideRef.current));
        }
        return;
      }

      if (payload.type === "shared-state-change") {
        sharedStateRef.current = payload.state || null;
        postToFrame(peerFrameRef, {
          type: "apply-shared-state",
          state: payload.state || null,
        });
        return;
      }

      if (payload.type === "district-selection-apply") {
        districtStateRef.current = Array.isArray(payload.districts) ? payload.districts : [];
        hexSelectionRef.current = { hexIds: [], activeHexId: "" };
        postToFrame(peerFrameRef, {
          type: "apply-district-selection",
          districts: districtStateRef.current,
        });
        return;
      }

      if (payload.type === "hex-selection-change") {
        hexSelectionRef.current = {
          hexIds: Array.isArray(payload.hexIds) ? payload.hexIds : [],
          activeHexId: String(payload.activeHexId || ""),
        };
        postToFrame(peerFrameRef, {
          type: "apply-hex-selection",
          selection: hexSelectionRef.current,
        });
        return;
      }

      if (payload.type === "viewport-change") {
        viewportStateRef.current = payload.viewport || null;
        postToFrame(peerFrameRef, {
          type: "set-viewport",
          viewport: payload.viewport || null,
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const activeMapLoad = Object.entries(mapLoadingBySide).find(([, state]) => state?.active);
  const lastMapLoad = activeMapLoad?.[1] || Object.values(mapLoadingBySide).find((state) => state?.last) || null;
  const mapLoadingState = lastMapLoad
    ? {
        ...lastMapLoad,
        active: Boolean(activeMapLoad),
        label: activeMapLoad ? `${activeMapLoad[0] === "left" ? "Левая карта" : "Правая карта"}: ${activeMapLoad[1].label || "данные карты"}` : lastMapLoad.label,
      }
    : null;

  return (
    <div className={`comparison-workspace${activeSharedDrawer ? " is-shared-open" : ""}`}>
      <div className="comparison-top-left-rail">
        <Button
          variant="dark"
          onClick={() => {
            setActiveSharedDrawer("settings");
            postToFrame(leftFrameRef, { type: "open-drawer", drawer: "settings" });
          }}
          className="buttons_custom_value"
        >
          <AiFillSetting className="icons_custom" /> Настройки
        </Button>

        <DynamicAnalysisChartsPanel data={chartsData} />
        <MapLoadIndicator state={mapLoadingState} />
      </div>

      <div
        className={`comparison-shared-backdrop${activeSharedDrawer ? " is-open" : ""}`}
        onClick={() => {
          setActiveSharedDrawer("");
          postToFrame(leftFrameRef, { type: "close-drawer" });
        }}
      />

      <div className="comparison-grid">
        <section className={`comparison-panel comparison-panel-left${activeSharedDrawer ? " is-raised" : ""}`}>
          <iframe
            ref={leftFrameRef}
            title="Левая карта сравнения"
            className="comparison-frame"
            src={buildFrameUrl("left")}
          />
        </section>

        <section className="comparison-panel comparison-panel-right">
          <iframe
            ref={rightFrameRef}
            title="Правая карта сравнения"
            className="comparison-frame"
            src={buildFrameUrl("right")}
          />
        </section>
      </div>
    </div>
  );
}
